import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonContent, IonHeader, IonTitle, IonRefresher, RefresherEventDetail, IonRefresherContent, IonSegment, IonSegmentButton, IonLabel, SegmentChangeEventDetail, NavController, AlertController } from '@ionic/angular/standalone';
import { IonRefresherCustomEvent, IonSegmentCustomEvent } from '@ionic/core';
import { OrderCardComponent } from 'src/app/components/elements/order-card/order-card.component';
import { PullService } from 'src/app/services/pull.service';
import { StateService } from 'src/app/services/state.service';
import { Order } from '@chemist2u/types-client/C2U/ParseObjects';
import { OrdersEmptyComponent } from './orders-empty/orders-empty.component';
import { PastPrescriptionCardComponent } from 'src/app/components/elements/past-prescription-card/past-prescription-card.component';
import { PastPrescriptionsEmptyComponent } from 'src/app/components/elements/past-prescription-card/past-prescriptions-empty/past-prescriptions-empty.component';
import { ICustomerMedicalProfile, TOrderPrescription, TOrderPrescriptionScriptItem, TOrderStatus } from '@chemist2u/types-client/C2U/Interfaces';
import { UtilService } from 'src/app/services/util.service';
import { Subscription } from 'rxjs';
import { LoginPlaceholderComponent } from 'src/app/components/elements/login-placeholder/login-placeholder.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AmplitudeService } from 'src/app/services/amplitude.service';

const ASegment = ["active_orders", "past_orders", "past_prescriptions"] as const;
type TSegment = typeof ASegment[number];

@Component({
    selector: 'app-orders',
    templateUrl: './orders.page.html',
    styleUrls: ['./orders.page.scss'],
    imports: [OrderCardComponent, IonContent, IonHeader, IonTitle, CommonModule, IonRefresher, IonRefresherContent, IonSegment, IonSegmentButton, IonLabel, OrdersEmptyComponent, PastPrescriptionCardComponent, PastPrescriptionsEmptyComponent, LoginPlaceholderComponent]
})
export class OrdersPage implements OnInit, AfterViewInit, OnDestroy {
  private $amplitude = inject(AmplitudeService);
  private $state = inject(StateService);
  private $pull = inject(PullService);
  private $util = inject(UtilService);
  private navController = inject(NavController);
  private alertController = inject(AlertController);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public isLoggedIn = this.$state.isLoggedIn;

  selectedSegment = signal<TSegment>("active_orders");
  segments = signal<{ value: TSegment, label: string }[]>([
    { value: "active_orders", label: "Active Orders" },
    { value: "past_orders", label: "Past Orders" },
    { value: "past_prescriptions", label: "Prescriptions" },
  ]);

  private readonly pastOrders = ["Delivered", "Cancelled", "Completed"];

  orders = signal<Order[]>([]);
  filteredOrders = signal<Order[]>([]);

  public combinedPrescriptions = computed(() => {
    const allowedOrderStatuses: TOrderStatus[] = ["Delivered", "Completed"];
    const prescriptions: TOrderPrescription[] = [];
    let orders = this.orders() || [];
    orders = orders.filter(order => order.status != undefined && allowedOrderStatuses.includes(order.status));
    orders.forEach(order => {
      let orderPrescriptions = order.prescriptions ?? [];
      orderPrescriptions = orderPrescriptions.filter(prescription => prescription.type == "electronic");
      prescriptions.push(...orderPrescriptions);
    });
    return this.$util.combinePrescriptionProfiles(prescriptions);
  });

  private ordersSub?: Subscription;

  ngOnInit() {
    this.$amplitude.track('NAVIGATE_ORDERS');
    this.ordersSub = this.$state.bOrders.subscribe(orders => {
      this.orders.set(orders);
      this.setFilteredOrders();
    });
  }

  ngOnDestroy() {
    if (this.ordersSub) this.ordersSub.unsubscribe();
  }

  ngAfterViewInit() {
    this.route.queryParams.subscribe(params => {
      const segment = params['segment'];
      if (segment && ASegment.includes(segment)) {
        this.selectedSegment.set(segment);
      }
    });
  }

  setFilteredOrders() {
    const orders = this.orders();
    const segment = this.selectedSegment();
    if (orders == undefined) {
      this.filteredOrders.set([]);
      return;
    }

    const filteredOrders = orders.filter(order => {
      if (order.status == undefined) return false;
      if (segment == "active_orders") {
        return !this.pastOrders.includes(order.status);
      } else if (segment == "past_orders") {
        return this.pastOrders.includes(order.status);
      } else {
        return false;
      }
    });

    const activeOrders = filteredOrders.sort((a: Order, b: Order) => {
      if (a.status === 'Ready' && b.status !== 'Ready') {
        return -1; // a comes before b
      }
      if (a.status !== 'Ready' && b.status === 'Ready') {
        return 1; // b comes before a
      }
      return 0; // no change in order
    });
    this.filteredOrders.set(activeOrders);
  }

  async doRefresh(event: IonRefresherCustomEvent<RefresherEventDetail>) {
    await this.$pull.orders();
    event.target.complete();
  }

  onSegmentChange(event: IonSegmentCustomEvent<SegmentChangeEventDetail>) {
    if (this.segments().filter(s => s.value == event.detail.value).length == 0) {
      throw "Segment value not allowed!";
    }
    const value: TSegment = event.detail.value as TSegment;
    this.selectedSegment.set(value);
    this.setFilteredOrders();
    this.$amplitude.track('ORDERS_SWITCH_SEGMENT', { segment: value });
  }

  goToOrder(order: Order) {
    this.navController.navigateForward(`/order/${order.id}`, { state: { order }});
  }

  goToScript(item: TOrderPrescriptionScriptItem) {
    // TODO: Implement this
    console.log("[OrdersPage] goToScript event", item);
  }

  async addPreviousScriptToCart(data: { item: TOrderPrescriptionScriptItem, profile: Parse.Object.ToJSON<ICustomerMedicalProfile> & Parse.JSONBaseAttributes}) {
    const alert = await this.alertController.create({
      header: 'Add to Cart',
      message: 'Would you like to add this script to your cart?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            console.log('Cancel clicked');
          }
        }, {
          text: 'Add',
        }
      ]
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if(role !== 'cancel') {
      console.log('[OrdersPage] addPreviousScriptToCart event', data);
      this.router.navigate(['/add-script'], { state: { item: data.item, profile: data.profile, type: "reorder" } });
    }
  }

}